import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { canEditSecretLevels, SECRET_LEVELS, SECRET_LEVEL_LABELS } from '@infras/shared-types';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { compareBy, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import { LevelBadge, LevelSelect } from '../components/SecretLevel';
import { useMe } from '../lib/useMe';
import { ENV_ORDER, type EnvName } from '../lib/envs';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';

const SECRETS = gql`
  query Secrets {
    secrets {
      id
      provider
      location
      name
      isBundle
      level
    }
  }
`;

interface Secret {
  id: string;
  provider: string;
  location?: string;
  name: string;
  isBundle: boolean;
  level: string;
}
interface Data {
  secrets: Secret[];
}

const LEVEL_RANK: Record<string, number> = { critical: 0, restricted: 1, normal: 2 };

// The 5 environment columns = the 5 GCP Secret Manager projects. Order + titles
// come from the shared lib so this list and the Secret-detail Keys table match.
const ENVS = ENV_ORDER;
type Env = EnvName;
const PROJECT_ENV: Record<string, Env> = {
  'dev-secrets-487112': 'dev',
  'sbx-secrets-488413': 'sandbox',
  'qa-secrets-488504': 'qa',
  'beta-secrets-488504': 'beta',
  'prod-secrets-488504': 'prod',
};
// A bundle's name embeds its sub-env (`foo--qa3a--keys`); collapse to the logical
// secret (`foo--keys`) so the same secret lines up across env columns.
const SUBENV_RE = /--(dev|sandbox|local|prod|qa3?[a-z]?|beta3?[a-z]?|staging3?[a-z]?)--keys$/i;
const canonical = (name: string, isBundle: boolean) =>
  isBundle ? name.replace(SUBENV_RE, '--keys') : name;

interface Row {
  name: string;
  isBundle: boolean;
  level: string;
  counts: Record<Env, number>;
}

/** Sort keys for the secrets table. `level` sorts by SEVERITY (critical first),
 *  not alphabetically — "critical, restricted, normal" is the useful order and
 *  the alphabetical one ("critical, normal, restricted") is nonsense. */
const SORT_PICK: Record<string, (r: Row) => unknown> = {
  name: (r) => r.name,
  level: (r) => LEVEL_RANK[r.level] ?? 2,
};

export function SecretsPage() {
  const { data, loading, error, refetch } = useQuery<Data>(SECRETS, { fetchPolicy: 'cache-and-network' });
  const { role } = useMe();
  const canEdit = !!role && canEditSecretLevels(role);
  // URL-backed so a filtered secret list is shareable (names only — never values)
  const [q, setQ] = useUrlState('q');
  const [levelFilter, setLevelFilter] = useUrlState('level');
  const [envFilterRaw, setEnvFilter] = useUrlState('env');
  const envFilter = (ENVS as readonly string[]).includes(envFilterRaw) ? (envFilterRaw as Env) : '';
  const sort = useUrlSort('name');
  const [page, setPage] = useUrlNumber('page', 1);

  const gcp = useMemo(() => (data?.secrets ?? []).filter((s) => s.provider === 'gcp'), [data]);

  const rows = useMemo(() => {
    const m = new Map<string, Row>();
    for (const s of gcp) {
      const env = s.location ? PROJECT_ENV[s.location] : undefined;
      if (!env) continue;
      const name = canonical(s.name, s.isBundle);
      let r = m.get(name);
      if (!r) {
        r = { name, isBundle: s.isBundle, level: s.level, counts: { dev: 0, sandbox: 0, qa: 0, beta: 0, prod: 0 } };
        m.set(name, r);
      }
      r.counts[env] += 1;
      r.isBundle = r.isBundle || s.isBundle;
      // logical row shows the WORST level across its env-instances
      if ((LEVEL_RANK[s.level] ?? 2) < (LEVEL_RANK[r.level] ?? 2)) r.level = s.level;
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [gcp]);

  // Which env columns have ANY data — i.e. actually scanned (dev/prod are
  // deferred, so their columns stay empty until GCP_SECRET_PROJECTS includes them).
  const scanned = useMemo(() => {
    const set = new Set<Env>();
    for (const r of rows) for (const e of ENVS) if (r.counts[e] > 0) set.add(e);
    return set;
  }, [rows]);

  const filtered = rows.filter(
    (r) =>
      (!q || r.name.toLowerCase().includes(q.toLowerCase())) &&
      // level = the row's WORST level across env-instances (same value the badge shows)
      (!levelFilter || r.level === levelFilter) &&
      // env = only secrets that exist in that environment's GCP project
      (!envFilter || r.counts[envFilter] > 0),
  );
  const sorted = compareBy(filtered, (r) => SORT_PICK[sort.key]?.(r), sort.dir);
  const pg = usePaged(sorted, 25, { page, onPage: setPage });
  const anyFilter = Boolean(q || levelFilter || envFilter);
  const clearFilters = () => {
    setQ('');
    setLevelFilter('');
    setEnvFilter('');
  };

  if (loading && !data)
    return (
      <div className="page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load the secret inventory — the API didn&apos;t respond.</p>
        <p className="muted">
          The secret-inventory scan keeps running in the background, so nothing is lost. If this
          persists, ask in #devops-infra.
        </p>
        <details style={{ marginTop: 12 }}>
          <summary className="muted">Details</summary>
          <p className="error">{error.message}</p>
        </details>
      </div>
    );

  const perEnv = (e: Env) => rows.filter((r) => r.counts[e] > 0).length;
  const unscanned = [...ENVS].filter((e) => !scanned.has(e));

  return (
    <div className="page">
      <div className="stat-row">
        <Stat label="Secrets" value={rows.length} />
        {ENVS.map((e) => (
          <Stat key={e} label={e} value={scanned.has(e) ? perEnv(e) : '—'} />
        ))}
      </div>

      <p className="muted text-xs" style={{ marginTop: 4 }}>
        Names only — no <strong>values</strong> stored. One row per logical secret; a tick = exists in
        that env's GCP project. Bundles (<code>--keys</code>) collapse per-env variants.
      </p>
      <p className="muted text-xs" style={{ marginTop: 4 }}>
        Filtered to your role's levels (L0 · L1 · L3)
        {canEdit && ' — reclassify via the level selector'}.
        {unscanned.length > 0 && (
          <>
            {' '}
            <strong>{unscanned.join(' / ')}</strong> not scanned yet.
          </>
        )}
      </p>

      <div className="list-panel">
      <div className="filter-row">
        <input placeholder="Filter by name…" value={q} onChange={(e) => setQ(e.target.value)} />
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
        <select
          className="class-select"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          title="filter by sensitivity level"
        >
          <option value="">all levels</option>
          {SECRET_LEVELS.map((l) => (
            <option key={l} value={l}>
              {SECRET_LEVEL_LABELS[l]} · {l}
            </option>
          ))}
        </select>
        <select
          className="class-select"
          value={envFilter}
          onChange={(e) => setEnvFilter(e.target.value as Env | '')}
          title="only secrets that exist in this environment"
        >
          <option value="">all environments</option>
          {ENVS.map((e) => (
            <option key={e} value={e}>
              {e}
              {scanned.has(e) ? '' : ' (not scanned)'}
            </option>
          ))}
        </select>
        <span className="filter-count">
          {filtered.length} of {rows.length}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="muted">
          No secrets yet. Run <code>pnpm --filter @infras/api scan secret-inventory</code>.
        </p>
      ) : (
        <table className="data-list--primary mtx">
          <thead>
            <tr>
              <SortHeader label="Secret" sortKey="name" sort={sort} />
              <SortHeader label="Level" sortKey="level" sort={sort} />
              {ENVS.map((e) => (
                <th key={e} className={`env-col ${scanned.has(e) ? '' : 'unscanned'}`} title={scanned.has(e) ? '' : 'not scanned yet'}>
                  {e}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((r) => (
              <tr key={r.name}>
                <td>
                  <Link className="table-title" to={`/secrets/${encodeURIComponent(r.name)}`}>
                    {r.name}
                  </Link>
                  {r.isBundle && <span className="badge" style={{ marginLeft: 6 }}>bundle</span>}
                </td>
                <td>
                  {canEdit ? (
                    <LevelSelect name={r.name} level={r.level} onChanged={() => void refetch()} />
                  ) : (
                    <LevelBadge level={r.level} />
                  )}
                </td>
                {ENVS.map((e) => (
                  <td key={e} className="env-col">
                    <Cell n={r.counts[e]} scanned={scanned.has(e)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total} start={pg.start} pageSize={pg.pageSize} onPage={pg.setPage} />
      </div>
    </div>
  );
}

function Cell({ n, scanned }: { n: number; scanned: boolean }) {
  if (n === 0) return <span className={scanned ? 'mtx-no' : 'mtx-unscanned'}>{scanned ? '·' : '–'}</span>;
  return <span className="mtx-yes">{n > 1 ? n : <Icon name="check" />}</span>;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
